import { getReferenceString, MedplumClient } from "@medplum/core";
import { ChargeItem, Procedure } from "@medplum/fhirtypes";

import isEqual from "lodash/isEqual";

import { ChargeItemProvenanceActivityCode } from "@/billing/constants/charge-item-provenance";
import { afterChargeItemWrite } from "@/billing/services/record-charge-item-provenance";
import { EncounterProceduresScope } from "@/encounters/constants/procedures";
import { QuiltedHealthIdentifierSystems } from "@/fhir/enums/quilted-health";
import { joinReferenceString, joinTokenString } from "@/utils/medplum";

import {
  SaveBillerSectionChargeEditFailureReason,
  SaveBillerSectionChargeEditResult,
} from "../types/biller-section-charge-edit";
import { ProcedureOwnership, StagedProceduresOperation } from "../types/procedures";
import { applyBillerSectionChargeEdit } from "../utils/biller-section-charge-edit";
import { ProceduresPickerFormType } from "../zod-schemas/procedures";

import { getOwnedEncounterProcedures, saveEncounterProceduresAndConditions } from "./procedures";

type SaveBillerSectionChargeEditParams = {
  medplum: MedplumClient;
  clientId: string;
  encounterId: string;
  /** The captured charge behind the edited row. The edit is recorded on this charge. */
  chargeItemId: string;
  /** The picker session: one locked procedure code and the diagnoses it ended up billed under. */
  operation: StagedProceduresOperation;
  isBiller: boolean;
};

const toFailureDetails = (error: unknown): string =>
  error instanceof Error ? error.message : "Unknown error";

const fail = (
  reason: SaveBillerSectionChargeEditFailureReason,
  details?: string,
): SaveBillerSectionChargeEditResult => ({ ok: false, reason, ...(details ? { details } : {}) });

/**
 * The session's diagnoses in link order, each as its system and code together. This is the form the
 * write compares them in.
 *
 * The system is part of the comparison because the same code in two systems is two different
 * diagnoses, and the write is what turns one into the other. The display is left out: it is a label
 * the value set can restate without the diagnosis having changed.
 */
const toDiagnosisIdentities = (
  conditions: ProceduresPickerFormType["conditions"],
): (string | undefined)[] => conditions.map(({ system, code }) => joinTokenString(system, code));

/** Whether a Procedure carries the given code. Handles a resource coded in more than one system. */
const hasProcedureCode = (procedure: Procedure, code: string): boolean =>
  Boolean(procedure.code?.coding?.some((coding) => coding.code === code));

/**
 * How this edit finds the Procedure its row owns: by the row's own charge, never by the code.
 *
 * Two rows can be billed under the same procedure code. One is a captured charge, the other an
 * entry a biller added on top of it with Add More. So the code only identifies a row's Procedure
 * for as long as nobody has done that. The charge id is the row's identity, and the key is built
 * from it.
 *
 * A row edited before the key existed points at a Procedure carrying nobody's key, which is named
 * as adoptable so the edit claims that one instead of creating a second Procedure beside it. Only
 * the biller section's own Procedures can be adopted: the write is scoped to
 * `EncounterProceduresScope.NonClinical`, and the provider's clinical Procedure is not in the set
 * the ownership is resolved against.
 */
const buildRowOwnership = (chargeItem: ChargeItem, chargeItemId: string): ProcedureOwnership => ({
  identifier: {
    system: QuiltedHealthIdentifierSystems.BillerSectionRowProcedureIdentifier,
    value: chargeItemId,
  },
  adoptableReferences: (chargeItem.supportingInformation ?? [])
    .map(({ reference }) => reference)
    .filter((reference): reference is string => Boolean(reference)),
});

/**
 * Writes one edit a biller made to a captured row: the diagnoses the code is billed under, the
 * Billing Note and Procedure Note, and the units and modifiers on the charge.
 *
 * Nothing the biller edits touches the encounter's clinical record. The diagnoses and notes are
 * written under `EncounterProceduresScope.NonClinical`. That scope marks them as the biller
 * section's and limits what the write compares against, so the clinical Procedure with the same
 * CPT code is invisible to it. The write then creates a biller-owned copy beside it instead of
 * rewriting the provider's. The diagnoses belong to that copy: a diagnosis the biller drops from
 * the row is retired by this write once no live Procedure points at it.
 *
 * That copy belongs to this row alone, keyed on its charge. See `buildRowOwnership`. Within the
 * biller section a code is not unique: a biller can add a second row under a code the encounter
 * already captured. Matching by code would then give both rows one Procedure, so editing either row
 * would replace the diagnoses, notes, and modifiers the other is billed under, and retire its
 * diagnosis with them.
 *
 * The charge is then pointed at that copy and carries the modifiers and both notes. Its original
 * `supportingInformation` reference is kept. See `applyBillerSectionChargeEdit` for why dropping it
 * would let a later capture charge the same procedure twice.
 *
 * An edit that changed no diagnoses and no notes writes no resources and only updates the charge.
 * Otherwise opening a row to change one modifier would create a biller-owned copy of every
 * diagnosis and note on it for nothing. The notes still reach the charge either way, which is where
 * the row reads them back from — see `Extensions.ChargeItemBillerNotes`.
 *
 * The charge is read and checked before anything is written, so an edit aimed at another
 * encounter's charge writes no resources at all.
 *
 * Safe to run more than once. The resource write finds the row's Procedure by its key and rewrites
 * it in place, the charge's Procedure reference is deduplicated instead of added again, and the
 * modifiers replace what is there instead of adding to it. A save retried after a partial failure
 * finishes the edit and changes nothing else.
 */
export const saveBillerSectionChargeEdit = async ({
  medplum,
  clientId,
  encounterId,
  chargeItemId,
  operation,
  isBiller,
}: SaveBillerSectionChargeEditParams): Promise<SaveBillerSectionChargeEditResult> => {
  if (!clientId.trim()) return fail("missing_client_id");
  if (!encounterId.trim()) return fail("missing_encounter_id");
  if (!chargeItemId.trim()) return fail("missing_charge_item_id");

  const { formValues, initialValues } = operation;
  const [editedProcedure] = formValues.procedures;
  const [initialProcedure] = initialValues.procedures;

  // The picker locks the code and never lets it be removed. A session without one means the caller
  // built the target wrong, not something a biller can cause.
  if (!editedProcedure?.code?.trim()) return fail("missing_procedure_code");

  let chargeItem;
  try {
    chargeItem = await medplum.readResource("ChargeItem", chargeItemId);
  } catch (error) {
    return fail("charge_read_failed", toFailureDetails(error));
  }

  // The charge was read by id alone, so nothing so far says it is one of this encounter's rows.
  // Recording the edit on a charge from another client or encounter would point that row at this
  // encounter's biller Procedure and replace its modifiers. Checked before the write, because the
  // row this edit is about is also what the resources it writes belong to.
  const patientReference = joinReferenceString("Patient", clientId);
  const encounterReference = joinReferenceString("Encounter", encounterId);

  if (
    chargeItem.subject?.reference !== patientReference ||
    chargeItem.context?.reference !== encounterReference
  ) {
    return fail(
      "charge_scope_mismatch",
      `ChargeItem ${chargeItemId} is not on ${patientReference} and ${encounterReference}`,
    );
  }

  const hasDiagnosisChanges = !isEqual(
    toDiagnosisIdentities(formValues.conditions),
    toDiagnosisIdentities(initialValues.conditions),
  );

  // A biller can change the Billing Note, or the Practitioner's Procedure Note, without touching a
  // diagnosis. The charge records both either way; this decides whether the row's own Procedure
  // copy is rewritten too, so the clinical-side record keeps up with what is being billed.
  const hasNoteChanges =
    editedProcedure.billerNotes !== initialProcedure?.billerNotes ||
    editedProcedure.providerNotes !== initialProcedure?.providerNotes;

  let procedureReference: string | undefined;

  if (hasDiagnosisChanges || hasNoteChanges) {
    const ownership = buildRowOwnership(chargeItem, chargeItemId);

    try {
      await saveEncounterProceduresAndConditions({
        medplum,
        clientId,
        encounterId,
        isBiller,
        formValues,
        initialValues,
        ownership,
        scope: EncounterProceduresScope.NonClinical,
      });
    } catch (error) {
      return fail("write_failed", toFailureDetails(error));
    }

    let ownedProcedures: Procedure[];
    try {
      // Read the procedure back instead of deriving it from the bundle: this is where it comes back
      // with the id the charge has to point at. Read by the row's key rather than by the code, so a
      // second row billed under the same code cannot be what the charge ends up pointing at.
      ownedProcedures = await getOwnedEncounterProcedures({
        medplum,
        encounterId,
        identifier: ownership.identifier,
      });
    } catch (error) {
      return fail("procedures_read_failed", toFailureDetails(error));
    }

    const ownedProcedure = ownedProcedures.find((procedure) =>
      hasProcedureCode(procedure, editedProcedure.code),
    );

    procedureReference = ownedProcedure ? getReferenceString(ownedProcedure) : undefined;

    // The write reported success but the read came back without the procedure. Pointing the charge
    // at it is what puts the biller's diagnoses on the row, so finishing without it would report a
    // saved edit the biller cannot see.
    if (!procedureReference) {
      return fail(
        "procedures_read_failed",
        `No procedure owned by ChargeItem ${chargeItemId} came back from the read`,
      );
    }
  }

  let updated: ChargeItem;
  try {
    updated = await medplum.updateResource(
      applyBillerSectionChargeEdit(chargeItem, {
        procedureReference,
        modifiers: editedProcedure.modifiers,
        quantity: editedProcedure.units,
        billerNotes: editedProcedure.billerNotes,
        providerNotes: editedProcedure.providerNotes,
      }),
    );
  } catch (error) {
    return fail("charge_write_failed", toFailureDetails(error));
  }

  return {
    ok: true,
    data: {
      chargeItem: await afterChargeItemWrite(
        medplum,
        updated,
        ChargeItemProvenanceActivityCode.Edited,
      ),
    },
  };
};
